import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import Button from '../../components/atoms/Button';
import TextField from '../../components/molecules/TextField';
import { SmallDialog } from '../../components/widgets';
import { DEFAULT_SCHEDULE, SCHEDULE_FREQUENCIES, TRANSLATION_KEY } from '../constants';
import { cronToPreset, getScheduleSummary, presetToCron } from '../utils';
import ScheduleFrequencyOptions from './ScheduleFrequencyOptions';
import ScheduleDaysPicker from './ScheduleDaysPicker';
import css from './ScheduleModal.css';

const MODAL_KEY = `${TRANSLATION_KEY}.SCHEDULE_MODAL`;

/**
 * Formats a schedule preset's time as the `HH:mm` value expected by the time input.
 *
 * @param {Object} preset - Schedule preset holding `hour` and optional `minute`
 * @returns {string} Zero-padded 24-hour time, e.g. `09:05`
 */
const formatPresetTime = ({ hour, minute = 0 }) =>
    `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

const ScheduleModal = ({ open, cron = null, isNew = false, onSave, onClose, saving = false, error = null }) => {
    const [preset, setPreset] = useState(DEFAULT_SCHEDULE);

    useEffect(() => {
        if (open) setPreset((cron && cronToPreset(cron)) || DEFAULT_SCHEDULE);
    }, [open, cron]);

    const selectFrequency = frequency =>
        setPreset(prev => ({
            ...prev,
            frequency,
            days: frequency === SCHEDULE_FREQUENCIES.WEEKLY && !prev.days.length ? DEFAULT_SCHEDULE.days : prev.days,
        }));

    const setTime = time => {
        const [hour, minute] = time.split(':').map(Number);
        if (Number.isInteger(hour) && Number.isInteger(minute)) {
            setPreset(prev => ({ ...prev, hour, minute }));
        }
    };

    const summary = getScheduleSummary(preset);
    const saveDisabled = saving || (preset.frequency === SCHEDULE_FREQUENCIES.WEEKLY && !preset.days.length);

    return (
        <SmallDialog open={open} onClose={onClose}>
            <div className={css.modal}>
                <div className={css.title}>
                    <Translate id={`${MODAL_KEY}.${isNew ? 'TITLE_NEW' : 'TITLE'}`} />
                </div>
                <ScheduleFrequencyOptions frequency={preset.frequency} onChange={selectFrequency} />
                {preset.frequency === SCHEDULE_FREQUENCIES.WEEKLY && (
                    <ScheduleDaysPicker days={preset.days} onChange={days => setPreset(prev => ({ ...prev, days }))} />
                )}
                {preset.frequency !== SCHEDULE_FREQUENCIES.HOURLY && (
                    <>
                        <div className={css.sectionLabel}>
                            <Translate id={`${MODAL_KEY}.TIME`} />
                        </div>
                        <TextField
                            type="time"
                            value={formatPresetTime(preset)}
                            containerClass={css.timeField}
                            onChange={setTime}
                        />
                    </>
                )}
                {error && <div className={css.error}>{error}</div>}
                <div className={css.footer}>
                    <div className={css.summary}>
                        <Translate id={`${MODAL_KEY}.${summary.key}`} data={summary.data} />
                    </div>
                    <div className={css.actions}>
                        <Button type="flat" onClick={onClose}>
                            <Translate id={`${MODAL_KEY}.CANCEL`} />
                        </Button>
                        <Button
                            type="primary"
                            disabled={saveDisabled}
                            showSpinner={saving}
                            onClick={() => onSave(presetToCron(preset))}
                        >
                            <Translate id={`${MODAL_KEY}.SAVE`} />
                        </Button>
                    </div>
                </div>
            </div>
        </SmallDialog>
    );
};

ScheduleModal.propTypes = {
    open: PropTypes.bool.isRequired,
    cron: PropTypes.string,
    isNew: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    saving: PropTypes.bool,
    error: PropTypes.string,
};

export default ScheduleModal;
